'use client';

import type { PropsWithChildren } from 'react';
import { useIsLoggedIn, useUser } from '@tv2/lib-offspring/api/user/UserProvider';
import { getPublicRuntimeEnv } from '@tv2/lib-offspring/libs/publicEnv';
import { useIsCurrentPageLoaded } from '@tv2/lib-offspring/components/PageContext';
import Sticky from '@tv2-cas/offspring/navigation/Sticky';
import { Navigation } from '@tv2-cas/offspring/navigation/Navigation';
import { useSearchLocationSync } from '@tv2-cas/offspring/search-page/api/searchQueryStore';
import { ServiceStatusMessages } from '@/components/messages/ServiceStatus/ServiceStatusMessages';
import { NotificationContainer } from '@/components/notifiers/Notification';
import { PageFooter } from '@/components/pageLayouts/PageFooter';
import { PageOverlays } from '@/components/pageLayouts/PageOverlays';

/**
 * Shared layout for all "main" content routes (everything except the full-screen
 * players, which live under `(player)`). The navigation bar and footer are
 * rendered here — once — so they are part of the shared layout that Next.js
 * keeps mounted across navigations between these routes, giving a persistent top
 * bar and footer that don't remount on navigation.
 */
export default function MainLayout({ children }: PropsWithChildren) {
  const { CHECKOUT_URL } = getPublicRuntimeEnv();
  const { completed } = useUser();
  const isLoggedIn = useIsLoggedIn();
  const isPageLoaded = useIsCurrentPageLoaded();
  const showMenu = completed && isLoggedIn;
  const showLogin = completed && !isLoggedIn;

  useSearchLocationSync();

  return (
    <>
      <Sticky>
        <Navigation showMenu={showMenu} showLogin={showLogin} />
      </Sticky>
      {isPageLoaded && (
        <>
          <ServiceStatusMessages />
          <NotificationContainer checkoutUrl={CHECKOUT_URL} />
          <PageOverlays />
        </>
      )}
      {children}
      {isPageLoaded && <PageFooter />}
    </>
  );
}
